import Phaser from 'phaser';
import type { SignalsPlugin } from '@repo/plugin-signals';
import type { TimeTrackerPlugin } from '@repo/plugin-timetracker';
import { Button, PauseModal, defaultTheme } from '@repo/ui';
import {
  GameState,
  GameMode,
  CellFlags,
  PRESETS,
  generatePuzzle,
  fillCell,
  clearCell,
  togglePencilMark,
  getPencilMark,
  autoFillPencilMarks,
  clearAllPencilMarks,
  getHint,
  moveSelection,
  setSelection,
  isImmutable,
  hasError,
  hasConstraintError,
  isConstraintSpent,
} from '../game';

/**
 * Type for scene with plugins
 */
interface SceneWithPlugins extends Phaser.Scene {
  signals: SignalsPlugin;
  timetracker: TimeTrackerPlugin;
}

/**
 * Extended text with constraint key for identifying constraints
 */
interface ConstraintText extends Phaser.GameObjects.Text {
  constraintKey?: string;
}

// Colors
const COLORS = {
  background: 0x1a1a2e,
  gridBackground: 0x16213e,
  cellBackground: 0x0f3460,
  cellSelected: 0x533483,
  cellImmutable: 0x1f4068,
  cellError: 0x8b0000,
  cellComplete: 0x228b22,
  textNormal: '#ffffff',
  textImmutable: '#ffdd44',
  textError: '#ff4444',
  pencilMark: '#888888',
  constraintNormal: '#ffffff',
  constraintError: '#ff4444',
  constraintSpent: '#444444',
  gridLine: 0x404040,
  gridLineBold: 0x666666,
};

/**
 * Main game scene for Unequal (Futoshiki)
 */
export class GameScene extends Phaser.Scene {
  private gameState!: GameState;
  private signals!: SignalsPlugin;
  private timetracker!: TimeTrackerPlugin;

  // Visual elements
  private gridContainer!: Phaser.GameObjects.Container;
  private cellGraphics: Phaser.GameObjects.Graphics[][] = [];
  private cellTexts: Phaser.GameObjects.Text[][] = [];
  private pencilMarkTexts: Phaser.GameObjects.Text[][][] = [];
  private constraintTexts: Phaser.GameObjects.Text[][] = [];
  private selectionRect!: Phaser.GameObjects.Graphics;

  // Layout
  private cellSize = 70;
  private gridX = 0;
  private gridY = 0;
  private constraintSize = 20;

  // Game settings
  private currentPreset = 0;
  private pencilMode = false;
  private autoPencilEnabled = false;

  // UI elements
  private statusText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private autoPencilCheckbox!: Phaser.GameObjects.Container;
  private pauseModal!: PauseModal;
  private timerUpdateEvent?: Phaser.Time.TimerEvent;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    const { width } = this.cameras.main;

    // Initialize plugins
    this.signals = (this as unknown as SceneWithPlugins).signals;
    this.timetracker = (this as unknown as SceneWithPlugins).timetracker;

    // Background
    this.cameras.main.setBackgroundColor(COLORS.background);

    // Setup pause/resume signal listeners
    this.setupPauseListeners();

    // Title
    const title = this.add.text(width / 2, 30, 'UNEQUAL', {
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);

    // Subtitle (mode)
    const preset = PRESETS[this.currentPreset]!;
    const modeText = preset.mode === GameMode.ADJACENT ? 'Adjacent Mode' : 'Greater-Than Mode';
    const subtitle = this.add.text(width / 2, 60, modeText, {
      fontSize: '16px',
      color: '#888888',
    });
    subtitle.setOrigin(0.5);

    // Create UI buttons
    this.createUIButtons();

    // Generate initial puzzle
    this.startNewGame();

    // Setup keyboard input
    this.setupKeyboardInput();

    // Start timer display update loop
    this.startTimerDisplay();
  }

  /**
   * Create UI buttons
   */
  private createUIButtons(): void {
    const { width, height } = this.cameras.main;

    // New Game button
    new Button(this, {
      x: width - 100,
      y: 50,
      width: 150,
      height: 40,
      text: 'New Game',
      fontSize: 16,
      theme: defaultTheme,
      variant: 'primary',
      onClick: () => this.startNewGame(),
    });

    // Hint button
    new Button(this, {
      x: width - 100,
      y: 100,
      width: 150,
      height: 40,
      text: 'Hint',
      fontSize: 16,
      theme: defaultTheme,
      variant: 'secondary',
      onClick: () => this.requestHint(),
    });

    // Auto Pencil checkbox
    this.createAutoPencilCheckbox(width - 100, 150);

    // Pencil Mode toggle
    new Button(this, {
      x: width - 100,
      y: 200,
      width: 150,
      height: 40,
      text: 'Pencil: OFF',
      fontSize: 16,
      theme: defaultTheme,
      variant: 'warning',
      onClick: () => this.togglePencilMode(),
    });

    // Pause button
    new Button(this, {
      x: width - 100,
      y: 250,
      width: 150,
      height: 40,
      text: 'Pause',
      fontSize: 16,
      theme: defaultTheme,
      variant: 'secondary',
      onClick: () => this.pauseGame(),
    });

    // Difficulty selector buttons
    const presetY = height - 60;
    const presetWidth = 100;
    const startX = 100;

    // Show first 4 presets
    for (let i = 0; i < Math.min(4, PRESETS.length); i++) {
      const presetItem = PRESETS[i]!;
      const nameParts = presetItem.name.split(' ');
      new Button(this, {
        x: startX + i * (presetWidth + 10),
        y: presetY,
        width: presetWidth,
        height: 35,
        text: (nameParts[0] ?? '') + ' ' + (nameParts[1] ?? ''),
        fontSize: 12,
        theme: defaultTheme,
        variant: i === this.currentPreset ? 'success' : 'secondary',
        onClick: () => this.selectPreset(i),
      });
    }

    // Status text
    this.statusText = this.add.text(width / 2, height - 25, 'Click a cell to select', {
      fontSize: '14px',
      color: '#888888',
    });
    this.statusText.setOrigin(0.5);

    // Timer text
    this.timerText = this.add.text(20, 20, 'Time: 0:00', {
      fontSize: '16px',
      color: '#ffffff',
    });
  }

  /**
   * Create auto pencil checkbox
   */
  private createAutoPencilCheckbox(x: number, y: number): void {
    this.autoPencilCheckbox = this.add.container(x, y);

    const boxSize = 24;
    const totalWidth = 150;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x333333, 1);
    bg.fillRoundedRect(-totalWidth / 2, -20, totalWidth, 40, 8);
    this.autoPencilCheckbox.add(bg);

    // Checkbox box
    const checkboxBg = this.add.graphics();
    checkboxBg.fillStyle(0x222222, 1);
    checkboxBg.fillRoundedRect(-totalWidth / 2 + 10, -boxSize / 2, boxSize, boxSize, 4);
    checkboxBg.lineStyle(2, 0x666666, 1);
    checkboxBg.strokeRoundedRect(-totalWidth / 2 + 10, -boxSize / 2, boxSize, boxSize, 4);
    this.autoPencilCheckbox.add(checkboxBg);

    // Checkmark (initially hidden)
    const checkmark = this.add.text(-totalWidth / 2 + 10 + boxSize / 2, 0, '✓', {
      fontSize: '18px',
      color: '#44ff44',
      fontStyle: 'bold',
    });
    checkmark.setOrigin(0.5);
    checkmark.setVisible(this.autoPencilEnabled);
    this.autoPencilCheckbox.add(checkmark);

    // Label
    const label = this.add.text(-totalWidth / 2 + 45, 0, 'Auto Pencil', {
      fontSize: '14px',
      color: '#ffffff',
    });
    label.setOrigin(0, 0.5);
    this.autoPencilCheckbox.add(label);

    // Make interactive
    const hitZone = this.add.zone(0, 0, totalWidth, 40);
    hitZone.setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', () => {
      this.autoPencilEnabled = !this.autoPencilEnabled;
      checkmark.setVisible(this.autoPencilEnabled);

      if (this.autoPencilEnabled) {
        // Fill pencil marks when enabled
        this.autoFillPencils();
        this.updateStatus('Auto pencil: ON - marks filled');
      } else {
        // Clear all pencil marks when disabled
        clearAllPencilMarks(this.gameState);
        this.renderGrid();
        this.updateStatus('Auto pencil: OFF - marks cleared');
      }
    });
    this.autoPencilCheckbox.add(hitZone);
  }

  /**
   * Start a new game
   */
  private startNewGame(): void {
    const preset = PRESETS[this.currentPreset]!;

    // Generate puzzle
    this.gameState = generatePuzzle(preset.order, preset.difficulty, preset.mode);

    // Auto-fill pencil marks if enabled
    if (this.autoPencilEnabled) {
      autoFillPencilMarks(this.gameState);
    }

    // Calculate layout
    this.calculateLayout();

    // Create grid visuals
    this.createGrid();

    // Emit game start signal (this triggers the timetracker to start)
    if (this.signals) {
      this.signals.global.lifecycle.gameStart.emit({
        timestamp: Date.now(),
        sceneKey: this.scene.key,
      });
    }

    this.updateStatus('New puzzle generated!');
  }

  /**
   * Calculate grid layout
   */
  private calculateLayout(): void {
    const { width, height } = this.cameras.main;
    const { order } = this.gameState;

    // Calculate cell size to fit the screen
    const maxGridWidth = width - 250; // Leave space for buttons
    const maxGridHeight = height - 150; // Leave space for title and footer

    // Include constraint spaces between cells
    const totalCellsWithConstraints = order + (order - 1) * 0.3;
    this.cellSize = Math.min(
      maxGridWidth / totalCellsWithConstraints,
      maxGridHeight / totalCellsWithConstraints,
      80
    );

    this.constraintSize = this.cellSize * 0.3;

    const gridWidth = order * this.cellSize + (order - 1) * this.constraintSize;
    const gridHeight = order * this.cellSize + (order - 1) * this.constraintSize;

    this.gridX = (width - 200 - gridWidth) / 2;
    this.gridY = (height - gridHeight) / 2 + 20;
  }

  /**
   * Create grid visual elements
   */
  private createGrid(): void {
    // Clear existing grid
    if (this.gridContainer) {
      this.gridContainer.destroy();
    }

    this.gridContainer = this.add.container(this.gridX, this.gridY);
    this.cellGraphics = [];
    this.cellTexts = [];
    this.pencilMarkTexts = [];
    this.constraintTexts = [];

    const { order, mode } = this.gameState;

    // Create cells
    for (let y = 0; y < order; y++) {
      this.cellGraphics[y] = [];
      this.cellTexts[y] = [];
      this.pencilMarkTexts[y] = [];

      for (let x = 0; x < order; x++) {
        const cellX = x * (this.cellSize + this.constraintSize);
        const cellY = y * (this.cellSize + this.constraintSize);

        // Cell background
        const graphics = this.add.graphics();
        this.cellGraphics[y]![x] = graphics;
        this.gridContainer.add(graphics);

        // Cell text (number)
        const text = this.add.text(
          cellX + this.cellSize / 2,
          cellY + this.cellSize / 2,
          '',
          {
            fontSize: `${this.cellSize * 0.6}px`,
            color: COLORS.textNormal,
            fontStyle: 'bold',
          }
        );
        text.setOrigin(0.5);
        this.cellTexts[y]![x] = text;
        this.gridContainer.add(text);

        // Pencil marks (3x3 grid for up to 9)
        this.pencilMarkTexts[y]![x] = [];
        const pencilSize = this.cellSize / 3.5;
        for (let n = 0; n < order && n < 9; n++) {
          const px = cellX + ((n % 3) + 0.5) * (this.cellSize / 3);
          const py = cellY + (Math.floor(n / 3) + 0.5) * (this.cellSize / 3);

          const pencilText = this.add.text(px, py, '', {
            fontSize: `${pencilSize}px`,
            color: COLORS.pencilMark,
          });
          pencilText.setOrigin(0.5);
          this.pencilMarkTexts[y]![x]![n] = pencilText;
          this.gridContainer.add(pencilText);
        }

        // Make cell interactive
        const zone = this.add.zone(cellX + this.cellSize / 2, cellY + this.cellSize / 2, this.cellSize, this.cellSize);
        zone.setInteractive({ hitArea: new Phaser.Geom.Rectangle(-this.cellSize / 2, -this.cellSize / 2, this.cellSize, this.cellSize), hitAreaCallback: Phaser.Geom.Rectangle.Contains, useHandCursor: true });
        zone.on('pointerdown', () => this.onCellClick(x, y));
        this.gridContainer.add(zone);
      }
    }

    // Create constraints between cells
    for (let y = 0; y < order; y++) {
      this.constraintTexts[y] = [];

      for (let x = 0; x < order; x++) {
        const cellFlags = this.gameState.flags[y * order + x] ?? 0;

        // Right constraint
        if (x < order - 1) {
          const hasRight = (cellFlags & CellFlags.ADJ_RIGHT) !== 0;
          if (hasRight || mode === GameMode.ADJACENT) {
            const cx = x * (this.cellSize + this.constraintSize) + this.cellSize + this.constraintSize / 2;
            const cy = y * (this.cellSize + this.constraintSize) + this.cellSize / 2;

            let symbol = '';
            if (hasRight) {
              symbol = mode === GameMode.ADJACENT ? '|' : '>';
            }

            const constraintText = this.add.text(cx, cy, symbol, {
              fontSize: `${this.constraintSize}px`,
              color: COLORS.constraintNormal,
              fontStyle: 'bold',
            });
            constraintText.setOrigin(0.5);
            this.gridContainer.add(constraintText);

            // Store with key for updates
            (constraintText as ConstraintText).constraintKey = `${x},${y},right`;
            this.constraintTexts[y]![x * 2] = constraintText;
          }
        }

        // Down constraint
        if (y < order - 1) {
          const hasDown = (cellFlags & CellFlags.ADJ_DOWN) !== 0;
          if (hasDown || mode === GameMode.ADJACENT) {
            const cx = x * (this.cellSize + this.constraintSize) + this.cellSize / 2;
            const cy = y * (this.cellSize + this.constraintSize) + this.cellSize + this.constraintSize / 2;

            let symbol = '';
            if (hasDown) {
              symbol = mode === GameMode.ADJACENT ? '-' : 'v';
            }

            const constraintText = this.add.text(cx, cy, symbol, {
              fontSize: `${this.constraintSize}px`,
              color: COLORS.constraintNormal,
              fontStyle: 'bold',
            });
            constraintText.setOrigin(0.5);
            this.gridContainer.add(constraintText);

            // Store with key for updates
            (constraintText as ConstraintText).constraintKey = `${x},${y},down`;
            this.constraintTexts[y]![x * 2 + 1] = constraintText;
          }
        }
      }
    }

    // Create selection rectangle
    this.selectionRect = this.add.graphics();
    this.gridContainer.add(this.selectionRect);

    // Initial render
    this.renderGrid();
  }

  /**
   * Render the grid based on current state
   */
  private renderGrid(): void {
    const { order, nums } = this.gameState;

    for (let y = 0; y < order; y++) {
      for (let x = 0; x < order; x++) {
        const cellX = x * (this.cellSize + this.constraintSize);
        const cellY = y * (this.cellSize + this.constraintSize);
        const value = nums[y * order + x] ?? 0;
        const immutable = isImmutable(this.gameState, x, y);
        const error = hasError(this.gameState, x, y);
        const selected = x === this.gameState.selectedX && y === this.gameState.selectedY;

        // Draw cell background
        const graphics = this.cellGraphics[y]?.[x];
        if (!graphics) continue;
        graphics.clear();

        let bgColor = COLORS.cellBackground;
        if (this.gameState.completed) {
          bgColor = COLORS.cellComplete;
        } else if (error) {
          bgColor = COLORS.cellError;
        } else if (selected) {
          bgColor = COLORS.cellSelected;
        } else if (immutable) {
          bgColor = COLORS.cellImmutable;
        }

        graphics.fillStyle(bgColor, 1);
        graphics.fillRoundedRect(cellX, cellY, this.cellSize, this.cellSize, 8);
        graphics.lineStyle(2, COLORS.gridLine, 1);
        graphics.strokeRoundedRect(cellX, cellY, this.cellSize, this.cellSize, 8);

        // Update cell text
        const text = this.cellTexts[y]?.[x];
        if (!text) continue;
        if (value > 0) {
          text.setText(value.toString());
          text.setColor(
            error ? COLORS.textError : (immutable ? COLORS.textImmutable : COLORS.textNormal)
          );
          text.setVisible(true);

          // Hide pencil marks when cell has value
          for (let n = 0; n < order && n < 9; n++) {
            this.pencilMarkTexts[y]?.[x]?.[n]?.setVisible(false);
          }
        } else {
          text.setVisible(false);

          // Show pencil marks
          for (let n = 0; n < order && n < 9; n++) {
            const pencilText = this.pencilMarkTexts[y]?.[x]?.[n];
            if (pencilText) {
              const hasMark = getPencilMark(this.gameState, x, y, n);
              pencilText.setText(hasMark ? (n + 1).toString() : '');
              pencilText.setVisible(hasMark);
            }
          }
        }
      }
    }

    // Update constraints
    for (let y = 0; y < order; y++) {
      for (let x = 0; x < order; x++) {
        // Right constraint error
        const constraintTextRight = this.constraintTexts[y]?.[x * 2];
        if (constraintTextRight) {
          const hasErrorRight = hasConstraintError(this.gameState, x, y, 1);
          const isSpentRight = isConstraintSpent(this.gameState, x, y, 1);
          constraintTextRight.setColor(
            hasErrorRight ? COLORS.constraintError : (isSpentRight ? COLORS.constraintSpent : COLORS.constraintNormal)
          );
        }

        // Down constraint error
        const constraintTextDown = this.constraintTexts[y]?.[x * 2 + 1];
        if (constraintTextDown) {
          const hasErrorDown = hasConstraintError(this.gameState, x, y, 2);
          const isSpentDown = isConstraintSpent(this.gameState, x, y, 2);
          constraintTextDown.setColor(
            hasErrorDown ? COLORS.constraintError : (isSpentDown ? COLORS.constraintSpent : COLORS.constraintNormal)
          );
        }
      }
    }

    // Update selection rectangle
    this.selectionRect.clear();
    if (!this.gameState.completed) {
      const selX = this.gameState.selectedX * (this.cellSize + this.constraintSize);
      const selY = this.gameState.selectedY * (this.cellSize + this.constraintSize);

      this.selectionRect.lineStyle(4, 0xffff00, 1);
      this.selectionRect.strokeRoundedRect(selX - 2, selY - 2, this.cellSize + 4, this.cellSize + 4, 10);
    }
  }

  /**
   * Handle cell click
   */
  private onCellClick(x: number, y: number): void {
    if (this.gameState.completed) return;

    setSelection(this.gameState, x, y);
    this.renderGrid();
  }

  /**
   * Setup keyboard input
   */
  private setupKeyboardInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState.completed) return;

      const key = event.key;
      const { order } = this.gameState;

      // Arrow keys - move selection
      if (key === 'ArrowUp') {
        moveSelection(this.gameState, 0, -1);
        this.renderGrid();
        return;
      }
      if (key === 'ArrowDown') {
        moveSelection(this.gameState, 0, 1);
        this.renderGrid();
        return;
      }
      if (key === 'ArrowLeft') {
        moveSelection(this.gameState, -1, 0);
        this.renderGrid();
        return;
      }
      if (key === 'ArrowRight') {
        moveSelection(this.gameState, 1, 0);
        this.renderGrid();
        return;
      }

      // Number keys - fill cell or toggle pencil mark
      const num = parseInt(key, 10);
      if (num >= 1 && num <= order) {
        const { selectedX, selectedY } = this.gameState;

        if (this.pencilMode || event.shiftKey) {
          togglePencilMark(this.gameState, selectedX, selectedY, num - 1);
        } else {
          fillCell(this.gameState, selectedX, selectedY, num);
          // Auto-update pencil marks if enabled
          if (this.autoPencilEnabled) {
            autoFillPencilMarks(this.gameState);
          }
        }

        this.renderGrid();
        this.checkWin();
        return;
      }

      // Delete/Backspace - clear cell
      if (key === 'Delete' || key === 'Backspace' || key === '0') {
        const { selectedX, selectedY } = this.gameState;
        clearCell(this.gameState, selectedX, selectedY);
        // Auto-update pencil marks if enabled
        if (this.autoPencilEnabled) {
          autoFillPencilMarks(this.gameState);
        }
        this.renderGrid();
        return;
      }

      // M - auto fill pencil marks
      if (key === 'm' || key === 'M') {
        this.autoFillPencils();
        return;
      }

      // H - hint
      if (key === 'h' || key === 'H') {
        this.requestHint();
        return;
      }

      // P - toggle pencil mode
      if (key === 'p' || key === 'P') {
        this.togglePencilMode();
        return;
      }
    });
  }

  /**
   * Toggle pencil mode
   */
  private togglePencilMode(): void {
    this.pencilMode = !this.pencilMode;
    this.updateStatus(this.pencilMode ? 'Pencil mode: ON' : 'Pencil mode: OFF');
  }

  /**
   * Setup pause modal - creates the PauseModal component which
   * automatically listens to pause/resume signals
   */
  private setupPauseListeners(): void {
    // Create PauseModal - it automatically subscribes to pause/resume signals
    this.pauseModal = new PauseModal(this, {
      theme: defaultTheme,
      title: 'PAUSED',
    });
  }

  /**
   * Pause the game
   */
  private pauseGame(): void {
    if (this.gameState.completed) return;
    this.timetracker?.pause();
  }

  /**
   * Auto-fill pencil marks
   */
  private autoFillPencils(): void {
    autoFillPencilMarks(this.gameState);
    this.renderGrid();
    this.updateStatus('Pencil marks filled');
  }

  /**
   * Request a hint
   */
  private requestHint(): void {
    const result = getHint(this.gameState);
    if (result) {
      setSelection(this.gameState, result.x, result.y);
      this.renderGrid();
      this.updateStatus('Hint revealed at selected cell');
      this.checkWin();
    } else {
      this.updateStatus('No hints available');
    }
  }

  /**
   * Select a preset
   */
  private selectPreset(index: number): void {
    this.currentPreset = index;
    this.startNewGame();
  }

  /**
   * Check for win condition
   */
  private checkWin(): void {
    if (this.gameState.completed) {
      // Stop the timer display updates
      this.stopTimerDisplay();

      const cheated = this.gameState.cheated;
      const time = this.timetracker?.getFormattedTime() ?? '0:00';
      const elapsedSeconds = this.timetracker?.getElapsedSeconds() ?? 0;

      this.updateStatus(
        cheated
          ? `Puzzle complete (with hints) - Time: ${time}`
          : `Congratulations! Puzzle solved - Time: ${time}`
      );

      // Emit game over signal
      if (this.signals) {
        this.signals.global.lifecycle.gameOver.emit({
          timestamp: Date.now(),
          sceneKey: this.scene.key,
          finalScore: elapsedSeconds,
          reason: 'puzzle_completed',
        });
      }

      // TODO: Show Scoreboard modal with final time
      // if (!cheated) { ... }
    }
  }

  /**
   * Update status text
   */
  private updateStatus(message: string): void {
    this.statusText.setText(message);
  }

  /**
   * Start timer display update loop
   * Updates the timer text every second using timetracker
   */
  private startTimerDisplay(): void {
    this.stopTimerDisplay();

    // Update immediately
    this.updateTimerDisplay();

    // Create update loop
    this.timerUpdateEvent = this.time.addEvent({
      delay: 1000,
      callback: () => this.updateTimerDisplay(),
      loop: true,
    });
  }

  /**
   * Stop timer display updates
   */
  private stopTimerDisplay(): void {
    if (this.timerUpdateEvent) {
      this.timerUpdateEvent.remove();
      this.timerUpdateEvent = undefined;
    }
  }

  /**
   * Update the timer display text from timetracker
   */
  private updateTimerDisplay(): void {
    const formattedTime = this.timetracker?.getFormattedTime() ?? '00:00';
    this.timerText.setText(`Time: ${formattedTime}`);
  }
}
